import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import {
	addLinkAssetPart,
	removePartLink,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useAssetPartsGridActionsAndButtons = ({
	gridRef,
	searchStatistics,
	searchAssetParts,
	setRemovePartError,
	includeDescendants,
}: {
	gridRef: GridApi | null;
	searchStatistics: Function;
	searchAssetParts: Function;
	setRemovePartError: (err: string) => void;
	includeDescendants: boolean;
}) => {
	const {
		state: { assetSearchId, listAssetParts, rowSelected },
		actions: {
			setFirstTime,
			setPartSearch,
			setDataPartSelected,
			setListAssetParts,
		},
	} = useAssetDetailsContext();
	const [searchParams] = useSearchParams();
	const [refreshTrigger, setRefreshTrigger] = useState(1);
	const isMountRef = useRef(true);

	// BUTTONS STATES
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			setFirstTime(true);
			setSaveButton(false);
			setDeleteOpen(false);
			setPartSearch("");
			setDataPartSelected(undefined);
			searchAssetParts(includeDescendants);
			searchStatistics();
		}
	}, [searchParams, assetSearchId, includeDescendants]);

	const addRow = useCallback(() => {
		setSaveButton(true);
		const newData = { id: -1, AssetId: assetSearchId };
		const currentData = listAssetParts ? listAssetParts : [];
		const data = [newData, ...currentData];
		setListAssetParts(data);
	}, [listAssetParts, assetSearchId]);

	const removeSelected = useCallback(async () => {
		if (listAssetParts) {
			const selectedRowNodes = gridRef?.getSelectedNodes();
			try {
				if (selectedRowNodes?.length) {
					const removePromises = selectedRowNodes.map(async (partLink) => {
						const removePart = await removePartLink(
							assetSearchId as any,
							partLink.data.Name,
						);
						if (!removePart.ok) {
							setRemovePartError(removePart.message);
							throw new Error(removePart.message);
						}
						return removePart;
					});

					await Promise.all(removePromises);
				} else if (rowSelected) {
					const removePart = await removePartLink(
						assetSearchId as any,
						rowSelected.Name,
					);
					if (!removePart.ok) {
						setRemovePartError(removePart.message);
						return;
					}
				}

				setRefreshTrigger((prev) => prev + 1);
			} catch (error) {
				console.error("Error removing parts:", error);
			}
		}
	}, [assetSearchId, refresh, gridRef, rowSelected]);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		addRow,
		removeSelected,
		saveButton,
		deleteOpen,
		setSaveButton,
		setDeleteOpen,
	};
};
